/**
 * Step 3 policy onboarding helpers — statutory conflict detection and
 * combined-path continue gating (defaults + selective PDF overrides).
 */

import type { ConflictDecision } from "../components/onboarding/PolicyConflictCard";
import type {
  PolicyExtractedItem,
  PolicyExtractionDraftFull,
} from "./api";

export const POLICY_OVERRIDE_DOMAINS = [
  { id: "leave_types", labelKey: "app.s3_domain_leave_types" },
  { id: "allowances", labelKey: "app.s3_domain_allowances" },
  { id: "grades", labelKey: "app.s3_domain_grades" },
  { id: "loan_products", labelKey: "app.s3_domain_loan_products" },
  { id: "excuse_types", labelKey: "app.s3_domain_excuse_types" },
  { id: "shifts", labelKey: "app.s3_domain_shifts" },
  { id: "approval_chains", labelKey: "app.s3_domain_approval_chains" },
  { id: "eos_rules", labelKey: "app.s3_domain_eos_rules" },
] as const;

export type PolicyOverrideDomainId =
  (typeof POLICY_OVERRIDE_DOMAINS)[number]["id"];

export interface DetectedConflict {
  key: string;
  label: string;
  customerValue: string;
  statutoryValue: string;
  article: string;
  overrideAllowed: boolean;
}

const STATUTORY_FLOORS = {
  annual_leave_days: { min: 21, article: "Article 109", label: "Annual leave (days/year)" },
  sick_leave_days_full_pay: {
    min: 30,
    article: "Article 117",
    label: "Sick leave at full pay (days/year)",
  },
  maternity_leave_weeks: {
    min: 10,
    article: "Article 151",
    label: "Maternity leave (weeks)",
  },
} as const;

function parsePositiveNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "" || raw === "—") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Scan extracted leave-type items for values below KSA statutory floors. */
export function detectStatutoryConflicts(
  draft: PolicyExtractionDraftFull | null | undefined,
): DetectedConflict[] {
  if (!draft?.items?.length) return [];

  const out: DetectedConflict[] = [];
  for (const item of draft.items) {
    if (item.domain !== "leave_types") continue;
    const payload = item.payload_json ?? {};
    const code = String(payload.code ?? "").toUpperCase();
    const category = String(payload.category ?? "").toLowerCase();
    const days = parsePositiveNumber(payload.number_of_days);

    if (days === null) continue;

    if (code === "ANNUAL" || category === "annual") {
      if (days < STATUTORY_FLOORS.annual_leave_days.min) {
        out.push({
          key: `annual_leave_${item.id}`,
          label: STATUTORY_FLOORS.annual_leave_days.label,
          customerValue: `${days} days`,
          statutoryValue: `${STATUTORY_FLOORS.annual_leave_days.min} days minimum`,
          article: STATUTORY_FLOORS.annual_leave_days.article,
          overrideAllowed: false,
        });
      }
      continue;
    }

    if (code === "SICK" || category === "sick") {
      if (days < STATUTORY_FLOORS.sick_leave_days_full_pay.min) {
        out.push({
          key: `sick_leave_${item.id}`,
          label: STATUTORY_FLOORS.sick_leave_days_full_pay.label,
          customerValue: `${days} days`,
          statutoryValue: `${STATUTORY_FLOORS.sick_leave_days_full_pay.min} days minimum`,
          article: STATUTORY_FLOORS.sick_leave_days_full_pay.article,
          overrideAllowed: false,
        });
      }
      continue;
    }

    if (code === "MATERNITY" || category === "maternity") {
      const weeks = days / 7;
      if (weeks < STATUTORY_FLOORS.maternity_leave_weeks.min) {
        out.push({
          key: `maternity_leave_${item.id}`,
          label: STATUTORY_FLOORS.maternity_leave_weeks.label,
          customerValue: `${weeks.toFixed(1)} weeks (${days} days)`,
          statutoryValue: `${STATUTORY_FLOORS.maternity_leave_weeks.min} weeks minimum`,
          article: STATUTORY_FLOORS.maternity_leave_weeks.article,
          overrideAllowed: false,
        });
      }
    }
  }
  return out;
}

export function allConflictsResolved(
  conflicts: DetectedConflict[],
  decisions: Record<string, ConflictDecision>,
): boolean {
  if (conflicts.length === 0) return true;
  return conflicts.every((c) => decisions[c.key] !== undefined);
}

/** Approved extracted items in selected domains that are not yet live. */
export function countApprovedUnpromotedInDomains(
  items: PolicyExtractedItem[] | undefined,
  domains: PolicyOverrideDomainId[],
): number {
  if (!items?.length || domains.length === 0) return 0;
  const domainSet = new Set(domains);
  return items.filter(
    (i) =>
      domainSet.has(i.domain as PolicyOverrideDomainId) &&
      (i.approval_status === "approved" || i.approval_status === "edited") &&
      !i.live_entity_id,
  ).length;
}

export interface CombinedContinueInput {
  defaultsReady: boolean;
  conflicts: DetectedConflict[];
  decisions: Record<string, ConflictDecision>;
  overrideDomains: PolicyOverrideDomainId[];
  draftCount: number;
  approvedUnpromoted: number;
}

export function isCombinedContinueBlocked(input: CombinedContinueInput): boolean {
  if (!input.defaultsReady) return true;
  if (!allConflictsResolved(input.conflicts, input.decisions)) return true;
  const startedOverride =
    input.overrideDomains.length > 0 && input.draftCount > 0;
  if (startedOverride && input.approvedUnpromoted > 0) return true;
  return false;
}
